/**
 * IconPickerContent - Main icon picker component
 * Allows selecting emojis, FontAwesome icons, or custom uploaded icons
 */

import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { CATEGORY_NAMES, searchEmojis, getCategoryIcon } from './data';
import { searchFAIcons } from './data/fontawesome';
import { ICON_COLORS } from './constants';
import { getRecentEmojis, getRecentIcons, addRecentEmoji, addRecentIcon } from './utils/recentItems';
import { useCustomIcons } from './hooks/useCustomIcons';
import { EmojisTabContent, IconsTabContent, CustomIconsTab } from './components';
import './styles/index.css';

const IconPickerContent = ({ currentIcon, onIconChange, onIconDeleted, onClose, isVisible, nodeColor }) => {
  // Tab & search state
  const [activeTab, setActiveTab] = useState('emojis');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedColor, setSelectedColor] = useState(currentIcon?.color || ICON_COLORS[0]);
  const [recentEmojis, setRecentEmojis] = useState([]);
  const [recentIcons, setRecentIcons] = useState([]);
  
  // Refs
  const pickerRef = useRef(null);
  const inputRef = useRef(null);
  const scrollRef = useRef(null);
  const categoryRefs = useRef({});

  // Custom icons hook
  const customIconsState = useCustomIcons(onIconDeleted);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 150);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Load data on mount
  useEffect(() => {
    if (isVisible) {
      setRecentEmojis(getRecentEmojis());
      setRecentIcons(getRecentIcons());
      setTimeout(() => inputRef.current?.focus(), 50);
      scrollRef.current?.scrollTo(0, 0);
      customIconsState.loadIcons();
    }
  }, [isVisible]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close handlers
  useEffect(() => {
    if (!isVisible) return;
    
    const handleClickOutside = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) onClose();
    };
    const handleEscape = (e) => { if (e.key === 'Escape') onClose(); };
    
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside, true);
      document.addEventListener('keydown', handleEscape);
    }, 10);
    
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside, true);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isVisible, onClose]);

  // Close when canvas starts panning (trackpad or mouse drag)
  useEffect(() => {
    if (!isVisible) return;

    const handlePanStart = () => onClose();
    window.addEventListener('flowchartpanstart', handlePanStart);
    return () => window.removeEventListener('flowchartpanstart', handlePanStart);
  }, [isVisible, onClose]);

  // Search results (memoized)
  const emojiSearchResults = useMemo(() => 
    debouncedSearch.trim() ? searchEmojis(debouncedSearch) : null
  , [debouncedSearch]);

  const iconSearchResults = useMemo(() => 
    debouncedSearch.trim() ? searchFAIcons(debouncedSearch) : null
  , [debouncedSearch]);

  // Resolved color for icons
  const resolvedColor = selectedColor === 'inherit' ? (nodeColor || '#9ca3af') : selectedColor;

  // Handlers
  const handleSelect = useCallback((icon) => {
    if (icon.type === 'emoji') {
      addRecentEmoji(icon.value);
      setRecentEmojis(getRecentEmojis());
    } else if (icon.type === 'fontawesome') {
      addRecentIcon(icon.value);
      setRecentIcons(getRecentIcons());
    }
    onIconChange(icon);
  }, [onIconChange]);

  const handleClear = useCallback(() => onIconChange(null), [onIconChange]);

  const jumpToCategory = useCallback((cat) => {
    const el = categoryRefs.current[cat];
    if (el && scrollRef.current) {
      scrollRef.current.scrollTo({ top: el.offsetTop - 8, behavior: 'smooth' });
    }
  }, []);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    scrollRef.current?.scrollTo(0, 0);
  };

  // Current values for selection highlighting
  const currentEmojiValue = currentIcon?.type === 'emoji' ? currentIcon.value : null;
  const currentIconValue = currentIcon?.type === 'fontawesome' ? currentIcon.value : null;

  return (
    <div className="icon-picker" ref={pickerRef} onWheel={e => e.stopPropagation()}>
      {/* Tabs */}
      <div className="icon-picker__tabs">
        {['emojis', 'icons', 'custom'].map(tab => (
          <button 
            key={tab}
            className={`icon-picker__tab ${activeTab === tab ? 'icon-picker__tab--active' : ''}`} 
            onClick={() => handleTabChange(tab)}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
        <button className="icon-picker__tab icon-picker__tab--clear" onClick={handleClear}>Clear</button>
        <button 
          className="icon-picker__close-btn" 
          onClick={onClose}
          title="Close"
          aria-label="Close icon picker"
        >
          ×
        </button>
      </div>

      {/* Search (not for custom tab) */}
      {activeTab !== 'custom' && (
        <div className="icon-picker__search-wrapper">
          <svg className="icon-picker__search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
          </svg>
          <input 
            ref={inputRef}
            className="icon-picker__search" 
            placeholder={`Search ${activeTab}...`}
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
          {searchQuery && <button className="icon-picker__search-clear" onClick={() => setSearchQuery('')}>×</button>}
        </div>
      )}

      {/* Color picker (icons tab or when FA icon selected) */}
      {(activeTab === 'icons' || currentIcon?.type === 'fontawesome') && (
        <div className="icon-picker__color-bar">
          <button 
            className={`icon-picker__color-btn icon-picker__color-btn--auto ${selectedColor === 'inherit' ? 'icon-picker__color-btn--selected' : ''}`}
            onClick={() => {
              setSelectedColor('inherit');
              if (currentIcon?.type === 'fontawesome') {
                onIconChange({ ...currentIcon, color: 'inherit' });
              }
            }}
            title="Match category color"
          >
            <span className="icon-picker__auto-indicator" style={{ backgroundColor: nodeColor || '#9ca3af' }} />
            Auto
          </button>
          {ICON_COLORS.slice(1).map(c => (
            <button 
              key={c}
              className={`icon-picker__color-btn ${selectedColor === c ? 'icon-picker__color-btn--selected' : ''}`}
              style={{ backgroundColor: c }}
              onClick={() => {
                setSelectedColor(c);
                if (currentIcon?.type === 'fontawesome') {
                  onIconChange({ ...currentIcon, color: c });
                }
              }}
            />
          ))}
        </div>
      )}

      {/* Scrollable content */}
      <div className="icon-picker__scroll" ref={scrollRef}>
        {activeTab === 'emojis' && (
          <EmojisTabContent
            searchResults={emojiSearchResults}
            recentEmojis={recentEmojis}
            currentValue={currentEmojiValue}
            categoryRefs={categoryRefs}
            onSelect={handleSelect}
          />
        )}

        {activeTab === 'icons' && (
          <IconsTabContent
            searchResults={iconSearchResults}
            recentIcons={recentIcons}
            currentValue={currentIconValue}
            resolvedColor={resolvedColor}
            selectedColor={selectedColor}
            categoryRefs={categoryRefs}
            onSelect={handleSelect}
          />
        )}

        {activeTab === 'custom' && (
          <CustomIconsTab
            {...customIconsState}
            currentIcon={currentIcon}
            onSelect={handleSelect}
            onUpload={customIconsState.handleUpload}
            onRequestDelete={customIconsState.requestDelete}
            onConfirmDelete={customIconsState.confirmDelete}
            onCancelDelete={customIconsState.cancelDelete}
            onClearError={customIconsState.clearUploadError}
            onRetryLoad={customIconsState.loadIcons}
          />
        )}
      </div>

      {/* Category bar (emojis only, when not searching) */}
      {activeTab === 'emojis' && !searchQuery && (
        <div className="icon-picker__category-bar">
          {CATEGORY_NAMES.map(cat => (
            <button key={cat} className="icon-picker__category-btn" onClick={() => jumpToCategory(cat)} title={cat}>
              {getCategoryIcon(cat)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default IconPickerContent;
